import React from 'react';
import { Section } from './components';

type GuidePage = 'overview' | 'gold' | 'forex' | 'diamond';
type Guide = {
  title: string;
  paragraphs: string[];
  questions: { question: string; answer: string }[];
  sources: { label: string; href: string }[];
};

const GOLD_SOURCE = { label: 'Giá vàng tại 24h.com.vn', href: 'https://www.24h.com.vn/gia-vang-hom-nay-c425.html' };
const FOREX_SOURCE = { label: 'Tỷ giá tại Vietcombank', href: 'https://www.vietcombank.com.vn/vi-VN/KHCN/Cong-cu-tien-ich/Ty-gia' };
const DIAMOND_SOURCE = { label: 'Công cụ giá StoneAlgo', href: 'https://www.stonealgo.com/diamond-price-calculator/' };
const GIA_SOURCE = { label: 'Tìm hiểu 4C tại GIA', href: 'https://4cs.gia.edu/en-us/4cs-of-diamond-quality/' };

const GUIDES: Record<GuidePage, Guide> = {
  overview: {
    title: 'Chọn đúng công cụ tham khảo',
    paragraphs: [
      'Bảng giá tập hợp ba nguồn dữ liệu phục vụ những nhu cầu khác nhau. Giá vàng trong nước giúp đối chiếu giá mua vào, bán ra theo thương hiệu và loại vàng. Bảng ngoại tệ hiển thị tỷ giá Vietcombank theo VND cho một đơn vị tiền tệ. Công cụ kim cương lấy ước tính bán lẻ quốc tế từ StoneAlgo theo thông số viên đá.',
      'Mở từng bảng để xem đơn vị, nguồn và thời điểm cập nhật. Các nguồn có lịch công bố riêng, nên số liệu giữa các công cụ có thể không cùng thời điểm. Khi nguồn chưa trả dữ liệu, trang sẽ hiển thị thông báo để bạn đối chiếu trực tiếp tại nguồn.',
      'Trước khi so sánh, hãy chọn cùng loại vàng, hình thức đổi ngoại tệ hoặc thông số kim cương. Giá tham khảo hỗ trợ việc đọc thông tin thị trường; giá giao dịch cụ thể còn cần xác nhận với đơn vị bán tại thời điểm thực hiện.',
    ],
    questions: [],
    sources: [GOLD_SOURCE, FOREX_SOURCE, DIAMOND_SOURCE],
  },
  gold: {
    title: 'Cách đọc bảng giá vàng',
    paragraphs: [
      'Dữ liệu vàng được tổng hợp từ 24h.com.vn qua nguồn cấp giá của An Thư. Mỗi nhóm là một thương hiệu; từng dòng thể hiện loại vàng được nguồn công bố. Đơn vị hiển thị là nghìn đồng/chỉ: chẳng hạn 15.000 tương ứng 15 triệu đồng cho một chỉ.',
      '“Mua vào” là mức đơn vị kinh doanh mua vàng từ khách; “Bán ra” là mức bán cho khách. Số tăng, giảm bên dưới giá phản ánh chênh lệch với mức ghi nhận trước trong nguồn dữ liệu. Hãy xem thời điểm cập nhật và đối chiếu cùng thương hiệu, loại vàng trước khi so sánh.',
    ],
    questions: [
      { question: 'Có thể dùng giá vàng miếng để tính giá trang sức?', answer: 'Cần đối chiếu hàm lượng vàng, trọng lượng và loại sản phẩm. Giá trang sức còn phụ thuộc tiền công, đá gắn kèm và chính sách từng đơn vị.' },
      { question: 'Vì sao giá giao dịch có thể khác bảng?', answer: 'Bảng phản ánh thời điểm nguồn công bố. Giá thực tế có thể thay đổi khi giao dịch; hãy xác nhận lại với đơn vị bán.' },
    ],
    sources: [GOLD_SOURCE],
  },
  forex: {
    title: 'Đọc đúng tỷ giá ngoại tệ',
    paragraphs: [
      'Bảng sử dụng nguồn tỷ giá công khai của Vietcombank, với đơn vị VND cho một đơn vị ngoại tệ. Bạn có thể tìm bằng mã tiền tệ như USD, EUR, JPY hoặc tên tiếng Việt. Thời điểm cập nhật là thời điểm ngân hàng công bố trong dữ liệu nguồn.',
      'Các cột được đọc từ phía ngân hàng: “Mua tiền mặt” khi ngân hàng mua ngoại tệ tiền mặt; “Mua chuyển khoản” khi mua ngoại tệ chuyển khoản; “Bán ra” khi ngân hàng bán ngoại tệ. Ví dụ, số VND tham khảo để mua 100 USD bằng 100 nhân với tỷ giá bán USD.',
    ],
    questions: [
      { question: 'Dấu “—” có nghĩa là tỷ giá bằng 0?', answer: 'Dấu này cho biết nguồn chưa công bố mức giá tương ứng. Bạn có thể mở trang Vietcombank để kiểm tra thêm.' },
      { question: 'Tỷ giá này áp dụng cho mọi ngân hàng?', answer: 'Đây là tỷ giá tham khảo của Vietcombank. Ngân hàng khác, phương thức giao dịch và thời điểm thực hiện có thể có mức khác.' },
    ],
    sources: [FOREX_SOURCE],
  },
  diamond: {
    title: 'Hiểu giá tham khảo kim cương',
    paragraphs: [
      'Công cụ lấy ước tính giá bán lẻ kim cương tự nhiên từ StoneAlgo theo hình dạng, carat, màu sắc và độ tinh khiết bạn chọn. Mở thông số nâng cao để điều chỉnh giác cắt, đối xứng, đánh bóng và huỳnh quang, rồi bấm xem giá để cập nhật kết quả.',
      'GIA đánh giá đặc điểm và chất lượng viên đá qua báo cáo kiểm định; GIA không công bố bảng giá kim cương. Số tiền trong công cụ là ước tính của StoneAlgo, cần phân biệt với thông tin chất lượng trên báo cáo GIA. Ngày dữ liệu nguồn được ghi cùng kết quả.',
    ],
    questions: [
      { question: 'Số tiền VND được tính thế nào?', answer: 'Giá USD được nhân với tỷ giá bán USD của Vietcombank khi có dữ liệu. Đây là quy đổi giá viên đá, chưa cộng vỏ trang sức hay tiền công.' },
      { question: 'Hai viên cùng 4C có luôn bằng giá?', answer: 'Ước tính không thay thế việc xem từng viên đá. Đặc điểm thực tế và điều kiện bán có thể tạo chênh lệch giá.' },
    ],
    sources: [DIAMOND_SOURCE, GIA_SOURCE, { label: 'GIA và thông tin giá', href: 'https://www.gia.edu/FAQ/gia-faq-about-gia-trade-wholesale-price-list' }],
  },
};

export const PriceGuide = ({ page }: { page: GuidePage }) => {
  const guide = GUIDES[page];
  return (
    <Section className="!tw-py-8">
      <div className="tw-border-0 tw-border-t tw-border-solid tw-border-jr-line tw-pt-7">
        <h2 className="tw-font-jr tw-m-0 tw-text-[26px] md:tw-text-[34px] tw-font-normal">{guide.title}</h2>
        <div className="tw-mt-5 tw-grid tw-gap-x-10 md:tw-grid-cols-2">
          {guide.paragraphs.map((paragraph) => <p key={paragraph} className="tw-mt-0 tw-mb-4 tw-text-[15px] tw-leading-relaxed tw-text-jr-mute">{paragraph}</p>)}
        </div>
        {guide.questions.length > 0 && <div className="tw-mt-2 tw-grid tw-gap-3 md:tw-grid-cols-2">
          {guide.questions.map(({ question, answer }) => <details key={question} className="tw-rounded-lg tw-border tw-border-solid tw-border-jr-line tw-bg-white tw-p-4">
            <summary className="tw-cursor-pointer tw-text-[15px] tw-font-medium tw-leading-relaxed tw-text-jr-green">{question}</summary>
            <p className="tw-mt-3 tw-mb-0 tw-text-[14px] tw-leading-relaxed tw-text-jr-mute">{answer}</p>
          </details>)}
        </div>}
        <p className="tw-mt-5 tw-mb-0 tw-text-[13px] tw-leading-relaxed tw-text-jr-mute">
          Nguồn đối chiếu:{' '}
          {guide.sources.map((source, index) => <React.Fragment key={source.href}>
            {index > 0 && ' · '}
            <a href={source.href} target="_blank" rel="noopener noreferrer" className="tw-font-medium tw-text-jr-green tw-underline tw-underline-offset-4">{source.label}</a>
          </React.Fragment>)}
        </p>
      </div>
    </Section>
  );
};
